import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Calendar, CalendarRange, Package, Info, ImagePlus, X, Loader2, ArrowLeft, Sparkles } from "lucide-react";
import { VideoInventory } from "@/components/VideoInventory";
import { AccessEditor, UnloadEditor, FloorPlanField, emptyAccess, remapUnload } from "@/components/MovePlan";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SuburbAutocomplete } from "@/components/SuburbAutocomplete";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { fmtDate, addDays } from "@/lib/format";
import { DatePickerAU } from "@/components/DatePickerAU";
import { toast } from "sonner";
import { loadDraft, clearDraft, remapDraftPaths } from "@/lib/guest";

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

function FlexStepper({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex gap-1.5">
        {[0, 1, 2, 3].map((n) => (
          <button key={n} type="button" data-testid={`${testid}-${n}`} onClick={() => onChange(n)}
            className={`h-9 w-9 rounded-lg text-sm font-semibold border transition-colors ${value === n ? "bg-kinetic text-white border-kinetic" : "border-border hover:border-kinetic/50"}`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PostJob() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", pickup_address: "", dropoff_address: "", pickup_suburb: "", pickup_state: "NSW", pickup_postcode: "", dropoff_suburb: "", dropoff_state: "NSW", dropoff_postcode: "", preferred_date: "", flex_days_before: 0, flex_days_after: 0, access_notes: "", inventory_description: "" });
  const [photos, setPhotos] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [access, setAccess] = useState(emptyAccess());
  const [movePlan, setMovePlan] = useState({ unload: {}, floor_plan: null });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  useEffect(() => {
    const draft = loadDraft();
    if (!draft?.rooms?.length) return;
    const gid = localStorage.getItem("mt_guest_id");
    (async () => {
      let inv = draft;
      if (gid) {
        try {
          const { data } = await api.post("/inventory/claim-guest", { guest_id: gid });
          if (draft.videos?.length) inv = remapDraftPaths(draft, data.mapping);
        } catch (e) { console.error("Guest video claim failed:", e); }
      }
      setInventory(inv);
      toast.success("Your saved inventory has been added to this move");
    })();
  }, []);
  const todayStr = new Date().toLocaleDateString("en-CA");

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.path]);
      }
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (status) => {
    setError("");
    if (!form.title || !form.pickup_address || !form.dropoff_address) {
      setError("Title, pickup and drop-off addresses are required.");
      return;
    }
    if (form.preferred_date && form.preferred_date < todayStr) {
      setError("Preferred date can't be in the past.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/jobs", { ...form, photos, inventory, access, move_plan: movePlan, status });
      clearDraft();
      toast.success(status === "draft" ? "Draft saved" : "Job posted — bids incoming!");
      navigate(`/jobs/${data.id}${status === "draft" ? "" : "?posted=1"}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>
        <h1 className="font-display text-3xl font-bold mb-1">Post a moving job</h1>
        <p className="text-muted-foreground mb-8">Fill in the details and let removalists compete for your move.</p>

        {error && <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-6"><Info className="h-4 w-4" /> {error}</div>}

        <form data-testid="job-post-form" onSubmit={(e) => { e.preventDefault(); submit("open"); }} className="space-y-6">
          <Card className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Job title</Label>
              <Input data-testid="job-title-input" value={form.title} onChange={upd("title")} placeholder="2-bedroom apartment move" />
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="flex items-center gap-2"><MapPin className="h-4 w-4 text-emerald-500" /> Pickup</Label>
                <AddressAutocomplete testid="pickup-address-input" value={form.pickup_address}
                  onChange={(v) => setForm((f) => ({ ...f, pickup_address: v }))}
                  onSelect={(r) => setForm((f) => ({ ...f, pickup_address: r.street || r.display, pickup_suburb: r.suburb, pickup_state: r.state, pickup_postcode: r.postcode, pickup_lat: r.lat, pickup_lng: r.lon }))}
                  placeholder="Start typing a street address…" />
                <SuburbAutocomplete testid="pickup-suburb-input" value={form.pickup_suburb ? `${form.pickup_suburb}${form.pickup_state ? ", " + form.pickup_state : ""}${form.pickup_postcode ? " " + form.pickup_postcode : ""}` : ""}
                  onSelect={(r) => setForm((f) => ({ ...f, pickup_suburb: r.suburb, pickup_state: r.state, pickup_postcode: r.postcode, pickup_lat: r.lat, pickup_lng: r.lon }))} />
              </div>
              <div className="space-y-3">
                <Label className="flex items-center gap-2"><MapPin className="h-4 w-4 text-kinetic" /> Drop-off</Label>
                <AddressAutocomplete testid="dropoff-address-input" value={form.dropoff_address}
                  onChange={(v) => setForm((f) => ({ ...f, dropoff_address: v }))}
                  onSelect={(r) => setForm((f) => ({ ...f, dropoff_address: r.street || r.display, dropoff_suburb: r.suburb, dropoff_state: r.state, dropoff_postcode: r.postcode, dropoff_lat: r.lat, dropoff_lng: r.lon }))}
                  placeholder="Start typing a street address…" />
                <SuburbAutocomplete testid="dropoff-suburb-input" value={form.dropoff_suburb ? `${form.dropoff_suburb}${form.dropoff_state ? ", " + form.dropoff_state : ""}${form.dropoff_postcode ? " " + form.dropoff_postcode : ""}` : ""}
                  onSelect={(r) => setForm((f) => ({ ...f, dropoff_suburb: r.suburb, dropoff_state: r.state, dropoff_postcode: r.postcode, dropoff_lat: r.lat, dropoff_lng: r.lon }))} />
              </div>
            </div>
            <div className="space-y-3">
              <Label className="flex items-center gap-2"><Calendar className="h-4 w-4" /> Preferred date</Label>
              <DatePickerAU min={todayStr} testid="preferred-date-picker" value={form.preferred_date} onChange={(v) => setForm((f) => ({ ...f, preferred_date: v }))} />
              {form.preferred_date && (
                <div className="rounded-lg border border-border p-4 space-y-3 bg-muted/30" data-testid="flex-dates-panel">
                  <p className="text-sm font-medium flex items-center gap-2"><CalendarRange className="h-4 w-4 text-kinetic" /> Date flexibility</p>
                  <p className="text-xs text-muted-foreground">Let removalists pick a day around your preferred date. Choose how many days earlier or later you'll accept (up to 3 each way).</p>
                  <div className="grid grid-cols-2 gap-6 max-w-sm">
                    <FlexStepper label="Days earlier" testid="flex-before" value={form.flex_days_before} onChange={(v) => setForm((f) => ({ ...f, flex_days_before: v }))} />
                    <FlexStepper label="Days later" testid="flex-after" value={form.flex_days_after} onChange={(v) => setForm((f) => ({ ...f, flex_days_after: v }))} />
                  </div>
                  <p className="text-xs text-muted-foreground" data-testid="flex-window-summary">
                    {(form.flex_days_before || form.flex_days_after)
                      ? <>Removalists can choose any day from <b className="text-foreground">{fmtDate(addDays(form.preferred_date, -form.flex_days_before))}</b> to <b className="text-foreground">{fmtDate(addDays(form.preferred_date, form.flex_days_after))}</b>.</>
                      : <>Fixed date — removalists must move on <b className="text-foreground">{fmtDate(form.preferred_date)}</b>.</>}
                  </p>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <div>
              <Label className="flex items-center gap-2 text-base"><Sparkles className="h-4 w-4 text-kinetic" /> AI video inventory</Label>
              <p className="text-sm text-muted-foreground mt-1">Film your rooms and we'll itemise your furniture and calculate cubic metres so removalists can quote accurately.</p>
            </div>
            <VideoInventory value={inventory} onChange={(next) => { setMovePlan((p) => ({ ...p, unload: remapUnload(inventory?.rooms, next?.rooms || [], p.unload) })); setInventory(next); }} />
          </Card>

          <Card className="p-6 space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Package className="h-4 w-4" /> Inventory description</Label>
              <Textarea data-testid="inventory-description-textarea" value={form.inventory_description} onChange={upd("inventory_description")} placeholder="1 queen bed, 1 3-seater sofa, fridge, ~15 boxes, washing machine…" rows={3} />
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <div>
              <Label className="flex items-center gap-2 text-base"><Info className="h-4 w-4" /> Access details</Label>
              <p className="text-sm text-muted-foreground mt-1">Stairs, lifts, parking, tight corners — add notes plus photos or a short video for each address.</p>
            </div>
            <AccessEditor value={access} onChange={setAccess} />
          </Card>

          <Card className="p-6 space-y-5" data-testid="post-move-plan">
            <div>
              <Label className="flex items-center gap-2 text-base"><MapPin className="h-4 w-4 text-kinetic" /> Move plan (optional)</Label>
              <p className="text-sm text-muted-foreground mt-1">Tell your removalist where each room goes in the new home. Only shared with the removalist you accept — you can change it later.</p>
            </div>
            <div className="space-y-2"><Label>Unload locations</Label><UnloadEditor rooms={inventory?.rooms} unload={movePlan.unload} onChange={(u) => setMovePlan((p) => ({ ...p, unload: u }))} /></div>
            <div className="space-y-2"><Label>New home floor plan</Label><FloorPlanField value={movePlan.floor_plan} onChange={(fp) => setMovePlan((p) => ({ ...p, floor_plan: fp }))} /></div>
          </Card>

          <Card className="p-6">
            <Label className="flex items-center gap-2 mb-3"><ImagePlus className="h-4 w-4" /> Photos</Label>
            <div className="flex flex-wrap gap-3">
              {photos.map((p, i) => (
                <div key={p} className="relative h-24 w-24 rounded-lg overflow-hidden border border-border">
                  <img src={`${API}/files/${p}`} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => setPhotos((ph) => ph.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label className="h-24 w-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-kinetic transition-colors">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin text-kinetic" /> : <ImagePlus className="h-6 w-6 text-muted-foreground" />}
                <input type="file" accept="image/*" multiple className="hidden" data-testid="photo-upload-input" onChange={onFiles} />
              </label>
            </div>
          </Card>

          <div className="flex gap-3">
            <Button type="submit" disabled={saving} data-testid="submit-job-btn" className="bg-kinetic hover:bg-kinetic-hover text-white h-11 px-6">{saving ? "Posting…" : "Post job & get bids"}</Button>
            <Button type="button" variant="outline" disabled={saving} onClick={() => submit("draft")} data-testid="save-draft-btn" className="h-11 px-6">Save as draft</Button>
          </div>
        </form>
      </main>
    </div>
  );
}
